import { useMemo } from "react";

import { styled } from "@linaria/react";
import { ImageBrokenIcon } from "@phosphor-icons/react";

import CodeEditor from "@gx/dls/editor/CodeEditor";
import { PropsWithTheme, withTheme } from "@gx/dls/theme";
import { ProjectArtifactResource } from "@gx/ts-contracts/services/ontology/resources/artifact_pb";

import ErrorLayout from "@/layouts/ErrorLayout";
import LoadingLayout from "@/layouts/LoadingLayout";

import { PROJECT_ARTIFACT_CONTENT_TYPE_TO_CODE_EDITOR_LANGUAGE_MAP } from "@/pages/projects/components/project/settings/artifacts/constants";

const ArtifactContentViewerWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0 0 6px 6px;

  & > div {
    flex: 1;
    min-height: 0;
    min-width: 0;
  }
`);

interface ArtifactContentViewerProps {
  artifact: ProjectArtifactResource;
  content: string | null;
  isLoading: boolean;
  error: Error | null;
}

const ArtifactContentViewer = ({
  artifact,
  content,
  isLoading,
  error,
}: ArtifactContentViewerProps) => {
  const lang = PROJECT_ARTIFACT_CONTENT_TYPE_TO_CODE_EDITOR_LANGUAGE_MAP[artifact.contentType];

  const renderContent = useMemo(() => {
    if (!content) return "";
    if (lang === "md") {
      return content.replace(/^\s*```(?:markdown)?\n?/, "").replace(/```$/, "");
    }
    return content;
  }, [content, lang]);

  return (
    <ArtifactContentViewerWrapper>
      {isLoading ? (
        <LoadingLayout />
      ) : error ? (
        <ErrorLayout
          icon={ImageBrokenIcon}
          header="Failed to load artifact"
          message="The artifact you are looking for does not exist"
        />
      ) : (
        <CodeEditor
          content={renderContent}
          lang={lang}
          borderRadius={"0 0 6px 6px"}
          isReadOnly
          noBorder
        />
      )}
    </ArtifactContentViewerWrapper>
  );
};

export default ArtifactContentViewer;
